import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import Question from './Question';
import styles from './FAQ.module.scss';
import { ReactComponent as IconHelp } from '../../../../../assets/images/icons/icon_help.svg';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { showNanoVariant } from '../../../../../components/NanoBanner/NanoBanner';
import { closeDecagonChat, openDecagonChat } from '../../../../../components/AppBar/NavBar';
import { series3CollarFAQChatButtonClick } from '../../../../../lib/analytics/events';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../../../../lib/growthbook';
import { useLocale, FiCountry, FiRegion } from '../../../../../contexts/LocaleContext';
import { isEnrolledInPositionDiscount } from '../../../../../lib/positionDiscount';
import { US_FAQS } from './FAQLocale/us';
import { CA_FAQS } from './FAQLocale/ca';
import { GB_FAQS } from './FAQLocale/gb';
import { ES_FAQS } from './FAQLocale/es';
import { EU_FAQS } from './FAQLocale/eu';
import { ULTRA_FAQS } from './FAQLocale/ultra';
import { FAQItem, FAQContext } from './FAQLocale/types';
import { ultraCollarId } from '../../../../../types';

/**
 * FAQs shown in every locale. Kept separate from country-specific lists so new
 * launch countries automatically inherit them without a code change.
 */
const GLOBAL_FAQS: FAQItem[] = [
  {
    id: 'supported-countries',
    titleKey: 'in_which_countries_does_my_fi_work',
    answerKey: 'fi_works_across_north_america_and_much_of_europe_answer',
    showWhen: (context) => context.seriesThreePlusExperience,
  },
];

interface FAQProps {
  containerRef?: React.RefObject<HTMLDivElement>;
  freeTrialEnabled?: boolean;
  collarId?: string;
}

export default function FAQ({ containerRef, freeTrialEnabled, collarId }: FAQProps) {
  const { t } = useTranslation();
  const { country, region } = useLocale();
  const decagonChatEnabled = useFeatureValue('decagon-chat-widget', false);
  const showDecagonChat = showNanoVariant() && decagonChatEnabled;

  const seriesThreePlusExperience = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('series-three-plus-experience', false)
    : false;

  const enrolledInPositionDiscount = isEnrolledInPositionDiscount();

  // Create context for conditional FAQ rendering
  const faqContext: FAQContext = {
    freeTrialEnabled,
    enrolledInPositionDiscount,
    seriesThreePlusExperience,
    collarId,
  };

  // Select FAQ configuration based on country
  const getFAQsForCountry = (): FAQItem[] => {
    switch (country) {
      case FiCountry.CA:
        return CA_FAQS;
      case FiCountry.GB:
        return GB_FAQS;
      case FiCountry.ES:
        return ES_FAQS;
      case FiCountry.US:
        return US_FAQS;
      default:
        return region === FiRegion.EU ? EU_FAQS : US_FAQS;
    }
  };

  // Ultra FAQs replace the rest entirely — Ultra is US-only and the country-specific lists are
  // Series 3+/Mini focused.
  const baseFAQs = collarId === ultraCollarId ? ULTRA_FAQS : [...GLOBAL_FAQS, ...getFAQsForCountry()];

  const visibleFAQs = baseFAQs.filter((faq) => {
    if (!faq.showWhen) return true;
    return faq.showWhen(faqContext);
  });

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.origin !== 'https://decagon.ai') return;

      const { data } = event;
      if (data.type === 'DECAGON_INACTIVE') {
        closeDecagonChat();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      closeDecagonChat();
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.container}>
      <h2 className={styles.heading}>{t('faq')}</h2>
      <div>
        {visibleFAQs.map((faq, index) => (
          <Question key={faq.id} title={t(faq.titleKey)} page="Collar" index={index}>
            {faq.answerContent ? (
              typeof faq.answerContent === 'function' ? (
                faq.answerContent(t)
              ) : (
                faq.answerContent
              )
            ) : faq.answerKey ? (
              <Trans i18nKey={faq.answerKey} values={{ plus: seriesThreePlusExperience ? '+' : '' }} />
            ) : null}
          </Question>
        ))}
      </div>
      {showDecagonChat && (
        <div className={styles.decagonChatContainer}>
          <IconHelp />
          <div className={styles.decagonChatTitle}>{t('something_else_on_your_mind')}</div>
          <button
            className={styles.decagonChatButton}
            onClick={() => {
              series3CollarFAQChatButtonClick();
              openDecagonChat();
            }}
          >
            {t('chat_with_fi')}
          </button>
        </div>
      )}
    </div>
  );
}
